import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { STATUS_META, STATUS_OPTIONS, LOST_REASONS } from "../lib/leadStatus";

export function StatusBadge({ status, className = "" }) {
  const m = STATUS_META[status] || STATUS_META.new;
  return (
    <span data-testid="lead-status-badge"
      className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${m.cls} ${className}`}>
      {m.label}
    </span>
  );
}

export function StatusSelect({ lead, onUpdated, className = "" }) {
  const [status, setStatus] = useState(lead.status || "new");
  const [lostMode, setLostMode] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setStatus(lead.status || "new"); }, [lead.status, lead.lead_id]);

  const patch = async (body) => {
    setSaving(true);
    try {
      const r = await api.patch(`/leads/${lead.lead_id}`, body);
      setStatus(r.data.lead.status);
      onUpdated && onUpdated(r.data.lead);
      toast.success("Lead status updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
      setStatus(lead.status || "new");
    } finally {
      setSaving(false);
      setLostMode(false);
    }
  };

  const onSel = (v) => {
    if (v === "lost") { setLostMode(true); return; }
    patch({ status: v });
  };

  const m = STATUS_META[status] || STATUS_META.new;
  return (
    <div className={`flex items-center gap-1.5 flex-wrap ${className}`} onClick={(e) => e.stopPropagation()}>
      <select data-testid="lead-status-select" value={status} disabled={saving}
        onChange={(e) => onSel(e.target.value)}
        className={`text-xs font-semibold rounded-lg border px-2 py-1 focus:outline-none cursor-pointer ${m.cls}`}>
        {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
      </select>
      {lostMode && (
        <select data-testid="lead-lost-reason" defaultValue="" autoFocus
          onChange={(e) => e.target.value && patch({ status: "lost", lost_reason: e.target.value })}
          className="text-xs rounded-lg border border-red-300 text-red-700 px-2 py-1 focus:outline-none">
          <option value="" disabled>Lost reason…</option>
          {LOST_REASONS.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
        </select>
      )}
    </div>
  );
}

export function StatusHistory({ leadId, refreshKey }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!leadId) return;
    api.get(`/leads/${leadId}/history`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [leadId, refreshKey]);

  if (!rows.length) return <p className="text-[10px] text-[#8696A0]">No status changes yet.</p>;
  return (
    <div className="space-y-2.5" data-testid="lead-status-history">
      {rows.slice().reverse().map((h) => (
        <div key={h.history_id} className="flex items-start gap-2 text-[11px]">
          <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#25D366] shrink-0" />
          <div className="min-w-0">
            <p className="text-[#111B21]">
              <span className="text-[#8696A0]">{STATUS_META[h.from]?.label || h.from}</span>
              {" → "}
              <span className="font-semibold">{STATUS_META[h.to]?.label || h.to}</span>
              <span className="text-[9px] text-[#8696A0]"> · {h.by}</span>
            </p>
            {h.reason && <p className="text-[#8696A0]">{h.reason}</p>}
            <p className="text-[9px] font-mono text-[#8696A0]">{new Date(h.created_at).toLocaleString()}</p>
          </div>
        </div>
      ))}
    </div>
  );
}
